import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnlineStatus } from '@talk2nick/ui';
import { observeSnapshotsInSync } from '@talk2nick/shared';

/** 재접속 후 이 시간이 지나도 동기화 신호가 없으면 진행바를 내린다 */
const RECONNECT_TIMEOUT_MS = 15_000;
/** "방금 동기화됨" 토스트 노출 시간 */
const SYNCED_TOAST_MS = 2_500;

/**
 * 연결 상태 UI (PLAN 5-8-6장).
 *
 * | 상태 | 표시 |
 * |---|---|
 * | 실시간 연결됨 | 없음 (표준) |
 * | 재접속 중 | 얇은 상단 진행바 + "연결 중…" |
 * | 오프라인 | 배너 "오프라인 — 메시지는 전송 대기열에 저장됩니다" |
 * | 동기화됨 | "방금 동기화됨" 토스트 (조용하게) |
 *
 * Firestore가 재접속/큐 flush를 자동 처리하므로, 여기서는 브라우저 online/offline
 * 이벤트와 onSnapshotsInSync 신호만 조합한다.
 */
export function ConnectionStatus() {
  const online = useOnlineStatus();
  const { t } = useTranslation();
  const [reconnecting, setReconnecting] = useState(false);
  const [synced, setSynced] = useState(false);
  /** 마운트 시점에 이미 오프라인이었는지 — 최초 렌더에서 진행바를 띄우지 않기 위함 */
  const wasOfflineRef = useRef(!online);

  useEffect(() => {
    if (!online) {
      wasOfflineRef.current = true;
      setReconnecting(false);
      return;
    }
    // 정상 온라인 상태(앱 시작 포함)에서는 아무것도 표시하지 않는다
    if (!wasOfflineRef.current) return;
    wasOfflineRef.current = false;

    setReconnecting(true);
    const unsubscribe = observeSnapshotsInSync(() => {
      setReconnecting(false);
      setSynced(true);
    });
    const timer = window.setTimeout(() => setReconnecting(false), RECONNECT_TIMEOUT_MS);

    return () => {
      unsubscribe();
      window.clearTimeout(timer);
    };
  }, [online]);

  useEffect(() => {
    if (!synced) return;
    const timer = window.setTimeout(() => setSynced(false), SYNCED_TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [synced]);

  if (!online) {
    return (
      <div
        role="status"
        className="flex flex-none items-center justify-center gap-2 bg-[var(--warning)]/20 px-3 py-1.5 text-xs text-[var(--text-primary)]"
      >
        <span aria-hidden>📴</span>
        <span>{t('status.offline')}</span>
        <span className="text-[var(--text-secondary)]">{t('status.offlineHint')}</span>
      </div>
    );
  }

  if (reconnecting) {
    return (
      <div role="status" className="flex-none">
        <div className="connection-bar" aria-hidden />
        <p className="bg-[var(--bg-app)] px-3 py-1 text-center text-xs text-[var(--text-secondary)]">
          {t('status.reconnecting')}
        </p>
      </div>
    );
  }

  if (synced) {
    return (
      <p
        role="status"
        aria-live="polite"
        className="flex-none bg-[var(--bg-app)] px-3 py-1 text-center text-xs text-[var(--text-secondary)]"
      >
        {t('status.synced')}
      </p>
    );
  }

  return null;
}
